import { useState, SyntheticEvent } from 'react';
import {
	IconButton,
	Popover,
	MenuItem,
	SvgIcon,
	ListItemIcon,
	ListItemText,
} from '@mui/material';
import EllipsisVerticalIcon from '@heroicons/react/24/solid/EllipsisVerticalIcon';
import PencilIcon from '@heroicons/react/24/solid/PencilIcon';
import TrashIcon from '@heroicons/react/24/solid/TrashIcon';

interface TableRowActionsProps<T> {
	row: T;
	onEdit?: (row: T) => void;
	onDelete?: (row: T) => void;
	customActions?: Array<{
		label: string;
		icon?: any;
		onClick: (row: T) => void;
		color?: string;
	}>;
}

export const TableRowActions = <T extends any>({
	row,
	onEdit,
	onDelete,
	customActions = [],
}: TableRowActionsProps<T>) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	const handleOpen = (event: SyntheticEvent<HTMLElement>) => {
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleEdit = () => {
		onEdit?.(row);
		handleClose();
	};

	const handleDelete = () => {
		onDelete?.(row);
		handleClose();
	};

	return (
		<>
			<IconButton size="small" onClick={handleOpen}>
				<SvgIcon fontSize="small">
					<EllipsisVerticalIcon />
				</SvgIcon>
			</IconButton>

			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				PaperProps={{
					sx: {
						width: 160,
						'& .MuiMenuItem-root': {
							px: 2,
							py: 1,
							typography: 'body2',
						},
					},
				}}
			>
				{onEdit && (
					<MenuItem onClick={handleEdit}>
						<ListItemIcon>
							<SvgIcon fontSize="small">
								<PencilIcon />
							</SvgIcon>
						</ListItemIcon>
						<ListItemText>Edit</ListItemText>
					</MenuItem>
				)}

				{onDelete && (
					<MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
						<ListItemIcon>
							<SvgIcon fontSize="small" color="error">
								<TrashIcon />
							</SvgIcon>
						</ListItemIcon>
						<ListItemText>Delete</ListItemText>
					</MenuItem>
				)}

				{customActions.map((action, index) => (
					<MenuItem
						key={index}
						onClick={() => {
							action.onClick(row);
							handleClose();
						}}
						sx={action.color ? { color: action.color } : undefined}
					>
						{action.icon && (
							<ListItemIcon>
								<SvgIcon fontSize="small">
									<action.icon />
								</SvgIcon>
							</ListItemIcon>
						)}
						<ListItemText>{action.label}</ListItemText>
					</MenuItem>
				))}
			</Popover>
		</>
	);
};
